/* eslint-disable complexity */
import type { RouteLocationNormalized } from 'vue-router'

import { ROUTE_NETWORK } from '~/contexts/network/router/routes'

import { router } from '~/router/router'

import type { CommunityRoute, CommunityType } from '~/entities/container/community/interfaces/community'
import { useUsersStore } from '~/entities/user/user/stores/users'

import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { contentsMap } from '~/features-shared/content/stores/contents-db'

import type { RoutesName } from '~/typings/vue-router'

/** Content regex record */
export const contentRegexRecord: Partial<Record<IContentModel, RegExp>> = {
  [IContentModel.DerivedPost]: /dt-post-(?<derivedTypeId>[^:]+):(?<contentId>[^/]+)/i,
  [IContentModel.DerivedIdea]: /dt-idea-(?<derivedTypeId>[^:]+):(?<contentId>[^/]+)/i,
  [IContentModel.Post]: /post:(?<contentId>[^/]+)/i,
  [IContentModel.Question]: /(?:iquestion|qnaquestion|question):(?<contentId>[^/]+)/i,
  [IContentModel.Idea]: /(?:idea|ideation):(?<contentId>[^/]+)/i,
  [IContentModel.Wiki]: /(?:wiki|wikitree):(?<contentId>[^/]+)/i,
  [IContentModel.Survey]: /survey:(?<contentId>[^/]+)/i,
  [IContentModel.Wedo]: /(?:wedo:|content=)(?<contentId>[^/]+)/i,
  [IContentModel.Media]: /media:(?<contentId>[^/]+)/i,
  [IContentModel.Conference]: /social-event:(?<contentId>[^/]+)/i,
}

/** Content type (facets) regex record */
export const contentTypeFacetRegexRecord: Partial<Record<IContentModel, RegExp>> = {
  [IContentModel.DerivedIdea]: /(?:ideaspipeline-dt-ideas|dt-ideas)-(?<derivedTypeId>\S+)/i,
  [IContentModel.DerivedPost]: /dt-posts-(?<derivedTypeId>\S+)/i,
  [IContentModel.Post]: /blog/i,
  [IContentModel.Media]: /media|gallery/i,
  [IContentModel.Question]: /questions/i,
  [IContentModel.Idea]: /ideaspipeline|ideas/i,
  [IContentModel.Wiki]: /wiki/i,
  [IContentModel.Survey]: /surveys/i,
  [IContentModel.Wedo]: /wedos/i,
  [IContentModel.Conference]: /social-events/i,
}

// regex to extract ids from hash string
export const CONTENT_REGEX = /[^:]+:[^/]+\/[^:]+:(?<contentId>\S+)/
export const COMMUNITY_REGEX = /community:(?<communityId>[^/]+)/i
export const DM_REGEX = /dm:(?<dmId>[^/]+)/i
export const COMMENT_REGEX = /comment:(?<commentId>[^/]+)/i
export const ANSWER_REGEX = /answer:(?<answerId>[^/]+)/i
export const REPLY_REGEX = /reply:(?<replyId>[^/]+)/i
export const MESSAGE_REGEX = /message:(?<messageId>[^/]+)/i
export const PEOPLE_REGEX = /people:(?<profileId>[^/]+)/i
export const RITUAL_REGEX = /ritual:(?<ritualId>[^/]+)/i

// Extracts content id and content type from hash string

export interface getContentDataFromHashReturn {
  contentId?: string
  contentType?: IContentModel
  derivedTypeId?: string
}
export function getContentDataFromHash(hash: string): getContentDataFromHashReturn {
  let contentType: IContentModel

  interface regexResult { derivedTypeId?: string, contentId: string }
  // find a route with content type + content id, eg: /community:1234/idea:9876
  for (contentType in contentRegexRecord) {
    const regex = contentRegexRecord[contentType]
    if (regex?.test(hash)) {
      const { derivedTypeId, contentId } = (hash.match(regex)?.groups ?? {}) as unknown as regexResult
      return { contentId, contentType, derivedTypeId }
    }
  }

  // find a route with only a content type, eg: /community:1234/ideaspipeline
  for (contentType in contentTypeFacetRegexRecord) {
    const regex = contentTypeFacetRegexRecord[contentType]
    if (regex?.test(hash)) {
      const { derivedTypeId } = (hash.match(regex)?.groups ?? {}) as unknown as regexResult
      return { contentType, derivedTypeId }
    }
  }

  const { contentId } = (hash.match(CONTENT_REGEX)?.groups ?? {}) as unknown as regexResult
  return { contentId }
}

/**
 * Extract comment/reply Id from v2 url hash string.
 * @param hash V2 URL hash string to be processed.
 */
function getcommentReplyData(hash: string) {
  interface regexResult { commentId?: string, replyId?: string, answerId?: string }

  const { commentId, answerId, replyId } = {
    ...(hash.match(COMMENT_REGEX)?.groups || {}) as unknown as regexResult,
    ...(hash.match(REPLY_REGEX)?.groups || {}) as unknown as regexResult,
    ...(hash.match(ANSWER_REGEX)?.groups || {}) as unknown as regexResult,
  }
  return { commentId, replyId, answerId }
}

/**
 * Extract SubjectUri of community or dm from v2 url hash string.
 * @param hash V2 URL hash string to be processed.
 * @param communityType Indicating the community type either 'community' or 'dm'
 * @returns SubjectUri
 */
function getCommunityDMSubjectUri(hash: string, communityType: CommunityType) {
  if (communityType === 'community') {
    const { communityId } = (hash.match(COMMUNITY_REGEX)?.groups || {})
    return communityId
  }
  const { dmId } = (hash.match(DM_REGEX)?.groups || {})
  return dmId
}

export function extractCommunityRouteData(hash: string, communityType: CommunityType): CommunityRoute {
  const communitySubjectUri = getCommunityDMSubjectUri(hash, communityType)
  const contentData = getContentDataFromHash(hash)
  const result = {
    communitySubjectUri,
    ...contentData,
    comment: undefined,
    reply: undefined,
    page: undefined,
    communityType,
  } as CommunityRoute
  if (REPLY_REGEX.test(hash) || COMMENT_REGEX.test(hash) || ANSWER_REGEX.test(hash)) {
    // Extract comment reply data
    const commentReplyData = getcommentReplyData(hash)
    return {
      ...result,
      comment: commentReplyData.commentId,
      reply: commentReplyData.replyId,
      answer: commentReplyData.answerId,
    }
  }
  else if (MESSAGE_REGEX.test(hash)) {
    const { messageId } = (hash.match(MESSAGE_REGEX)?.groups || {})
    return { ...result, messageId }
  }
  else if (hash.includes('members')) {
    return { ...result, page: 'members' }
  }
  else if (hash.includes('settings')) {
    return { ...result, page: 'settings' }
  }
  else if (hash.includes('stats')) {
    return { ...result, page: 'statistics' }
  }
  return result
}

export function extractProfileRouteData(hash: string) {
  const { profileId } = (hash.match(PEOPLE_REGEX)?.groups || {})
  return profileId
}

export function extractRitualRoutedata(hash: string) {
  const { ritualId } = (hash.match(RITUAL_REGEX)?.groups || {})
  return ritualId
}

// eslint-disable-next-line max-statements
export function communityV3toV2Url(route: RouteLocationNormalized) {
  const _name = route.name

  if (!_name.startsWith('community-thread_id') && !_name.startsWith('dm-thread_id'))
    return ''

  const communityType = _name.startsWith('community-thread_id') ? 'community' : 'dm'
  const communityId = ('thread_id' in route.params && (route.params.thread_id as string).split(':')
    .pop()) || undefined

  if ('content_type' in route.params) {
    if (route.params.content_type === IContentModel.Post)
      return `${communityType}:${communityId}/blog` // Exception for post...
    return `${communityType}:${communityId}/${route.params.content_type}`
  }
  if (_name.endsWith('-thread_id-index-media'))
    return `${communityType}:${communityId}/gallery`
  if (_name.endsWith('-thread_id-index-idea'))
    return `${communityType}:${communityId}/ideaspipeline`
  if (_name.endsWith('-thread_id-index-dt-idea-:derived_type_id') && 'derived_type_id' in route.params)
    return `${communityType}:${communityId}/ideaspipeline-dt-ideas-${route.params.derived_type_id}`
  if (_name.endsWith('-thread_id-index-dt-post-:derived_type_id') && 'derived_type_id' in route.params)
    return `${communityType}:${communityId}/dt-posts-${route.params.derived_type_id}`
  if (_name.endsWith('-thread_id-index-question'))
    return `${communityType}:${communityId}/questions`
  if (_name.endsWith('-thread_id-index-wedo'))
    return `${communityType}:${communityId}/wedos`
  if (_name.endsWith('-thread_id-index-wiki'))
    return `${communityType}:${communityId}/wiki`
  if (_name.endsWith('-thread_id-settings'))
    return `${communityType}:${communityId}/settings`
  if (_name.endsWith('-thread_id-statistics'))
    return `${communityType}:${communityId}/stats`
  if (_name.endsWith('-thread_id-members'))
    return `${communityType}:${communityId}/members`
  return `${communityType}:${communityId}`
}

export function networkV2toV3Url(route: RouteLocationNormalized) {
  if (route.name === ROUTE_NETWORK.ACTIVITY || route.name === ROUTE_NETWORK.INVITATIONS) {
    const storeUsers = useUsersStore()
    return `people:${storeUsers.currentUser?.hash_key}/connections`
  }

  if (route.name === ROUTE_NETWORK.USER_ACTIVITY) {
    return `people:${route.params.thread_id}/activities`
  }

  if (route.name === ROUTE_NETWORK.USER_NETWORK) {
    return `people:${route.params.thread_id}/connections`
  }

  if (route.name === ROUTE_NETWORK.USER_PROFILE) {
    return `people:${route.params.thread_id}/aboutme`
  }
}

// eslint-disable-next-line max-statements
export function v3ToV2Url(route: RouteLocationNormalized) {
  const _name = route.name as RoutesName
  if (route.query?.content) {
    const communityType = (route.name as string)?.startsWith('dm-') ? 'dm' : 'community'
    const content = contentsMap.get(router.getQuery(route, 'content')!)
    if (content && !content.derived_type?.id) {
      return `${content.community_type || communityType}:${content.community_id}/${content.model}:${content.id}`
    }
    else if (content && content.derived_type?.id) {
      const type = content.model === IContentModel.DerivedIdea ? IContentModel.Idea : IContentModel.Post

      return `${content.community_type || communityType}:${content.community_id}/dt-${type}-${content.derived_type.id}:${content.id}`
    }
  }

  if (route.query?.media) {
    const communityType = (route.name as string)?.startsWith('dm-') ? 'dm' : 'community'
    const content = contentsMap.get(router.getQuery(route, 'media')!)
    if (content)
      return `${content.community_type || communityType}:${content.community_id}/media:${content.id}`
  }

  if (_name.startsWith('community-thread_id') || _name.startsWith('dm-thread_id'))
    return communityV3toV2Url(route)

  if (_name === 'home')
    return 'home'

  if (_name === 'me-bookmarks')
    return 'mybookmarks'

  if (_name === 'me-profile-index') {
    const storeUsers = useUsersStore()
    return `people:${storeUsers.currentUser?.hash_key}/aboutme`
  }

  if (_name.startsWith('me-contributions')) {
    const storeUsers = useUsersStore()
    return `people:${storeUsers.currentUser?.hash_key}/activities`
  }

  if (_name.startsWith('network'))
    return networkV2toV3Url(route)
}
